import type { FirebaseMessaging } from '@firebase/messaging-types';
import {
    ANONYM_USER,
    IS_ATTACHES,
    IS_AUTO_TEST_HIDE,
    IS_BAD_BROWSER,
    IS_BIZ_USER,
    IS_MAIL_POPUP,
    IS_MOBILE_BROWSER,
    IS_MY_TEAM,
    IS_ONPREMISE,
    IS_PWA,
    IS_REACT_BUY,
    IS_REACT_PROMO_NEW_YEAR,
    IS_REACT_PROMO_QUOTA_PAGE,
    IS_VKT_DESKTOP,
    IS_VKT_WEBVIEW,
    IS_WEBVIEW,
} from 'reactApp/appHelpers/configHelpers';
import { webpushConfig } from 'reactApp/appHelpers/featuresHelpers/features/webPush';
import { safetyPersonalLocalStorage } from 'reactApp/storage';
import { renderWebPushScreen, renderWebPushTooltip } from 'reactApp/ui/WebPush/WebPush.toolkit';
import { sendXray } from 'reactApp/utils/ga';

import { type Provider, api, FEATURE_NAME, log, XRAY_BLOCKED_SCREEN, XRAY_BLOCKED_TOOLTIP, XRAY_PERMISSION } from './shared';
import { initWebPushConsoleApi } from './webPush.console';
import { initDeeplinkBridge } from './webPush.deeplink';
import { initFcmMessaging, sendFcmTokenToServer } from './webPush.fcm';
import {
    getSentSubscriptionKey,
    getStoredProvider,
    getSubscriptionHash,
    getWebPushAccounts,
    isTokenSentToServer,
    setStoredProvider,
    setTokenSentToServer,
    trackSubscriptionSent,
} from './webPush.storage';
import { getActiveSwRegistration } from './webPush.sw';

/**
 * Снять чужую подписку при смене провайдера (cutover FCM<->pushme).
 * Один слот pushManager = одна подписка: без реклейма subscribe() на другом ключе
 * кинет InvalidStateError. Маркер надёжнее сравнения applicationServerKey (его отдают
 * не все браузеры). Старый endpoint после unsubscribe мёртв — сервер прунит по 410.
 */
async function reclaimSlotIfProviderChanged(reg: ServiceWorkerRegistration, current: Provider) {
    if (getStoredProvider() === current) {
        return;
    }

    try {
        const old = await reg.pushManager.getSubscription();

        if (old) {
            await old.unsubscribe();
        }
    } catch (error) {
        log('Reclaim slot failed. ', error);
    }

    setTokenSentToServer(false);
}

type UnsubscribeResult = 'unsubscribed' | 'no-subscription' | 'error';

async function unsubscribePush(): Promise<UnsubscribeResult> {
    try {
        const reg = await getActiveSwRegistration();
        const sub = await reg.pushManager.getSubscription();

        if (!sub) {
            log('No push subscription to unsubscribe.');
            return 'no-subscription';
        }

        await sub.unsubscribe();
        log('Push subscription unsubscribed.');
        return 'unsubscribed';
    } catch (error) {
        log('Unable to unsubscribe push. ', error);
        return 'error';
    }
}

function renderWebPushBlockedTooltip(isCloudBlocked = false) {
    if (IS_ATTACHES || IS_REACT_PROMO_QUOTA_PAGE) {
        return;
    }

    if (!webpushConfig.enableBlockedTooltip) {
        return;
    }

    sendXray([FEATURE_NAME, XRAY_BLOCKED_TOOLTIP, 'show']);
    renderWebPushTooltip({
        isBlocked: true,
        onAction: () => {
            sendXray([FEATURE_NAME, XRAY_BLOCKED_TOOLTIP, 'click']);
            sendXray([FEATURE_NAME, XRAY_BLOCKED_SCREEN, 'show']);
            renderWebPushScreen({
                isCloudBlocked,
                onClose: () => {
                    sendXray([FEATURE_NAME, XRAY_BLOCKED_SCREEN, 'close']);
                },
            });
        },
        onClose: () => {
            sendXray([FEATURE_NAME, XRAY_BLOCKED_TOOLTIP, 'close']);
            safetyPersonalLocalStorage.setItem(`${FEATURE_NAME}|closed-until`, String(Date.now() + 30 * 24 * 60 * 60 * 1000));
        },
    });
}

/** Отправить pushme-подписку (endpoint + webpush_keys) на сервер (дедуп по sha1(endpoint + emails)). */
async function sendPushSubscriptionToServer(sub: PushSubscription) {
    const accounts = await getWebPushAccounts();
    const json = sub.toJSON(); // { endpoint, keys: { p256dh, auth }, expirationTime }
    const endpoint = json.endpoint ?? '';
    const p256dh = json.keys?.p256dh;
    const auth = json.keys?.auth;

    // Без ключей подписка нерабочая — не шлём undefined, чтобы не маскировать проблему.
    if (!endpoint || !p256dh || !auth) {
        log('Push subscription is missing endpoint/keys, skip sending.', json);
        return;
    }

    const subscriptionHash = getSubscriptionHash(endpoint, accounts);
    const accountsEmails = accounts.map((account) => ({
        email: account.email,
    }));

    if (isTokenSentToServer(subscriptionHash)) {
        log("Pushme subscription (endpoint + keys) already sent to server so won't send it again unless it changes");
        return;
    }

    log('Sending pushme subscription (endpoint + keys) to server...');

    try {
        const res = await api.makeRequest({
            settings: {},
            platform: 'webpush',
            endpoint,
            webpush_keys: {
                p256dh,
                auth,
            },
            push_tags: ['Web'],
            accounts: accountsEmails,
        });

        setTokenSentToServer(subscriptionHash);
        trackSubscriptionSent('pushme');
        log('Pushme subscription sent, status:', res?.status);
    } catch (error) {
        log('Failed to send pushme subscription. ', error);
        setTokenSentToServer(false);
    }
}

/**
 * Подписаться на pushme. Если в слоте уже лежит подписка с ДРУГИМ ключом (напр. старая
 * FCM/`gcm_sender_id` из manifest.json), браузер кидает InvalidStateError и требует
 * unsubscribe → resubscribe — так и делаем.
 */
async function subscribePushme(reg: ServiceWorkerRegistration): Promise<PushSubscription> {
    const options = {
        userVisibleOnly: true,
        applicationServerKey: webpushConfig.vapidKeys.pushme,
    };

    try {
        return await reg.pushManager.subscribe(options);
    } catch (error) {
        if ((error as DOMException)?.name !== 'InvalidStateError') {
            throw error;
        }

        log('Existing subscription with different applicationServerKey, resubscribing.', error);

        const old = await reg.pushManager.getSubscription();

        if (old) {
            await old.unsubscribe();
        }

        setTokenSentToServer(false);

        return reg.pushManager.subscribe(options);
    }
}

// Коалесинг одновременных вызовов updateToken. requestPermission (грант через нашу кнопку)
// и permissions.onchange (тот же переход в granted) стреляют почти одновременно — без гварда
// это двойной getToken/subscribe. Пока запрос в полёте, повторный вызов ждёт тот же промис.
// eslint-disable-next-line init-declarations
let updateTokenInFlight: Promise<void> | undefined;

function updateToken(messaging?: FirebaseMessaging): Promise<void> {
    if (updateTokenInFlight) {
        return updateTokenInFlight;
    }

    updateTokenInFlight = performUpdateToken(messaging).finally(() => {
        updateTokenInFlight = undefined;
    });

    return updateTokenInFlight;
}

async function performUpdateToken(messaging?: FirebaseMessaging) {
    log('Updating push registration, provider:', webpushConfig.provider);

    // fcm без messaging (напр. ручной app.webPush.resubscribe при выключенной/неинициализированной
    // фиче) переподписаться не может — выходим, не проваливаясь в ветку «No token» с blocked-tooltip.
    if (webpushConfig.provider === 'fcm' && !messaging) {
        log('FCM messaging is not initialized, skip update.');
        return;
    }

    try {
        const serviceWorkerRegistration = await getActiveSwRegistration();
        const { provider } = webpushConfig;

        // При смене провайдера снять чужую подписку — иначе subscribe() на другом
        // VAPID-ключе кинет InvalidStateError (один слот pushManager).
        await reclaimSlotIfProviderChanged(serviceWorkerRegistration, provider);

        if (provider === 'fcm' && messaging) {
            const currentToken = await messaging.getToken({
                vapidKey: webpushConfig.vapidKeys.fcm,
                serviceWorkerRegistration,
            });

            if (currentToken) {
                log('Got FCM token', currentToken);
                await sendFcmTokenToServer(currentToken);
                setStoredProvider('fcm');
                return;
            }
        }

        if (provider === 'pushme') {
            const sub = await subscribePushme(serviceWorkerRegistration);

            if (sub) {
                await sendPushSubscriptionToServer(sub);
                setStoredProvider('pushme');
                return;
            }
        }

        log('No Instance ID token available. Request permission to generate one.');
        setTokenSentToServer(false);
        renderWebPushBlockedTooltip(true);
    } catch (error) {
        log('An error occurred while retrieving token. ', error);
        setTokenSentToServer(false);
    }
}

async function checkNotificationState(messaging?: FirebaseMessaging) {
    try {
        if (!('Notification' in window)) {
            log("Browser don't support notifications");
            return;
        }

        if (Notification.permission === 'default') {
            log("Notifications doesn't configured");
            return;
        }

        if (Notification.permission === 'granted') {
            const storedProvider = getStoredProvider();

            log(
                `checkNotificationState: permission=granted, storedProvider=${storedProvider ?? 'none'}, configProvider=${
                    webpushConfig.provider
                }`
            );

            // Нет маркера (первая подписка) либо провайдер сменился — маркер/кэш не годятся,
            // форсим updateToken (внутри reclaim освободит слот, если это cutover).
            if (storedProvider !== webpushConfig.provider) {
                log(
                    storedProvider
                        ? `Provider changed (${storedProvider} -> ${webpushConfig.provider}), re-subscribing.`
                        : `No stored provider, subscribing (${webpushConfig.provider}).`
                );
                await updateToken(messaging);
                return;
            }

            const accounts = await getWebPushAccounts();
            const sentKey = await getSentSubscriptionKey(accounts);
            const alreadySent = Boolean(sentKey && isTokenSentToServer(sentKey));

            log(`checkNotificationState: provider unchanged, sentKey=${sentKey ?? 'none'}, alreadySent=${alreadySent}`);

            if (alreadySent) {
                log('Notifications already enabled');
            } else {
                log('Sent key mismatch/absent — re-registering.');
                await updateToken(messaging);
            }
            return;
        }

        if (Notification.permission === 'denied') {
            log('Notifications denied');
            setTokenSentToServer(false);
            await invalidateToken(messaging);
            renderWebPushBlockedTooltip();
        }
    } catch (error) {
        log('checkNotificationState failed. ', error);
    }
}

/**
 * Инвалидировать подписку без перевыпроса: используется при потере permission,
 * когда перевыпрашивать токен бессмысленно (getToken/subscribe всё равно упадёт на denied).
 * В отличие от deleteToken, не зовёт updateToken.
 */
async function invalidateToken(messaging?: FirebaseMessaging) {
    try {
        if (webpushConfig.provider === 'pushme') {
            await unsubscribePush();
        } else if (messaging) {
            await messaging.deleteToken();
        }
        log('Token invalidated.');
    } catch (error) {
        log('Unable to invalidate token. ', error);
    }
}

/**
 * Реагировать на смену Notification.permission в рантайме (без перезагрузки).
 * granted -> перерегистрируем подписку и шлём свежий токен на сервер.
 * denied  -> сбрасываем кэш и инвалидируем токен.
 */
async function subscribePermissionChange(messaging?: FirebaseMessaging) {
    if (!('permissions' in navigator)) {
        return;
    }

    try {
        // eslint-disable-next-line compat/compat
        const permissionStatus = await navigator.permissions.query({ name: 'notifications' });

        permissionStatus.onchange = () => {
            if (permissionStatus.state === 'granted') {
                log('Permission changed to granted, re-subscribing.');
                setTokenSentToServer(false);
                updateToken(messaging).catch(log);
            } else if (permissionStatus.state === 'denied') {
                log('Permission changed to denied, invalidating token.');
                setTokenSentToServer(false);
                invalidateToken(messaging).catch(log);
                renderWebPushBlockedTooltip();
            }
        };
    } catch (error) {
        log('subscribePermissionChange failed. ', error);
    }
}

async function requestPermission(messaging?: FirebaseMessaging) {
    log('Requesting permission...');

    if (!('Notification' in window)) {
        log("Browser don't support notifications");
        return;
    }

    try {
        // eslint-disable-next-line compat/compat
        const permission = await window.Notification.requestPermission();

        sendXray([FEATURE_NAME, XRAY_PERMISSION, permission]);

        if (permission === 'granted') {
            log('Notification permission granted.');
            await updateToken(messaging);
        } else {
            log('Unable to get permission to notify.');
            renderWebPushBlockedTooltip(true);
        }
    } catch (error) {
        sendXray([FEATURE_NAME, XRAY_PERMISSION, 'error']);
        log('Failed to get permission to notify.');
        renderWebPushBlockedTooltip(true);
        log(error);
    }
}

async function deleteToken(messaging?: FirebaseMessaging) {
    log('Deleting token...');

    try {
        if (webpushConfig.provider === 'pushme') {
            await unsubscribePush();
        } else if (messaging) {
            await messaging.deleteToken();
        }
        log('Token deleted.');
        setTokenSentToServer(false);
        await updateToken(messaging);
    } catch (error) {
        log('Unable to delete token. ', error);
    }
}

/** Дёрнуть переоценку очереди 3promo (сразу либо по готовности менеджера). */
function triggerPromoManagerUpdate() {
    if (window.__3promoManager) {
        log('Trigger 3promo manager update');
        window.__3promoManager.update();
    } else {
        log('3promo manager not ready, defer update');
        window.addEventListener(
            'promo-manager-ready',
            () => {
                log('3promo manager ready, update');
                window.__3promoManager?.update();
            },
            { once: true }
        );
    }
}

// eslint-disable-next-line init-declarations
let activeMessaging: FirebaseMessaging | undefined;

/**
 * Регистрируем консольный API рано и безусловно (даже если фича выключена) — чтобы
 * app.webPush.status() всегда работал для диагностики. Экшены читают activeMessaging
 * в момент вызова (в fcm он появляется после initFcmMessaging в setupWebPushRuntime).
 */
function registerConsoleApi() {
    initWebPushConsoleApi({
        resubscribe: () => deleteToken(activeMessaging),
        unsubscribe: async () => {
            const result = await unsubscribePush();

            if (result === 'unsubscribed') {
                setTokenSentToServer(false);
            }

            return { unsubscribed: result === 'unsubscribed', reason: result };
        },
        requestPermission: () => requestPermission(activeMessaging),
    });
}

/**
 * Рантайм-обвязка обоих провайдеров: window.__WP, проверка состояния, подписка на смену
 * permission, апдейт 3promo. Для pushme messaging не нужен. Консольный API уже
 * зарегистрирован в initializeWebPush — здесь лишь прокидываем messaging для его экшенов.
 */
function setupWebPushRuntime(messaging?: FirebaseMessaging) {
    activeMessaging = messaging;

    window.__WP = {
        requestPermission: () => requestPermission(messaging),
    };

    checkNotificationState(messaging).catch(log);
    subscribePermissionChange(messaging).catch(log);

    triggerPromoManagerUpdate();
}

// eslint-disable-next-line max-lines-per-function, complexity, sonarjs/cognitive-complexity
export async function initializeWebPush() {
    log('Start webpush initialization');

    // Мост открытия нативного приложения по клику на пуш ставим ДО гейтов ниже
    // Гейты отключают лишь ПРИОБРЕТЕНИЕ подписки
    // Но пользователь мог подписаться раньше и всё ещё получать пуши — клик по ним должен открыть приложение независимо
    initDeeplinkBridge();

    if (
        IS_BAD_BROWSER ||
        IS_AUTO_TEST_HIDE ||
        IS_MAIL_POPUP ||
        IS_REACT_BUY ||
        IS_REACT_PROMO_NEW_YEAR ||
        IS_MY_TEAM ||
        IS_VKT_WEBVIEW ||
        IS_VKT_DESKTOP ||
        ANONYM_USER ||
        IS_WEBVIEW
    ) {
        log('Unsupported env');
        return;
    }

    // Консольная диагностика доступна даже при выключенной фиче (app.webPush.status()).
    registerConsoleApi();

    const { users, platforms, enablePromoTooltip: enabled } = webpushConfig;

    if (!enabled) {
        log('Disabled because 3promo tooltip disabled');
        return;
    }

    /**
     * Используем флаги из SSR, а не селекторы,
     * так как на момент инициализации reduxStore еще не готов
     */
    const userTypeAllowed = (IS_BIZ_USER && users.biz) || (IS_ONPREMISE && users.b2b) || (!IS_BIZ_USER && !IS_ONPREMISE && users.b2c);
    if (!userTypeAllowed) {
        log('Disabled by user type');
        return;
    }

    /**
     * На PWA IS_MOBILE_BROWSER=true, поэтому проверяем и на него, и на PWA
     */
    const isMobile = IS_MOBILE_BROWSER && !IS_PWA;
    const isPWA = IS_MOBILE_BROWSER && IS_PWA;
    const platformAllowed = (isMobile && platforms.touch) || (isPWA && platforms.pwa) || (!isMobile && !isPWA && platforms.desktop);
    if (!platformAllowed) {
        log('Disabled by platform');
        return;
    }

    // pushme: нативный Web Push без firebase SDK — не грузим увесистый чанк.
    // FCM-подписку (если была) снимет reclaimSlotIfProviderChanged нативно внутри updateToken.
    if (webpushConfig.provider === 'pushme') {
        log('Provider path: pushme (native)');

        if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
            log('Native web push not supported (serviceWorker/PushManager/Notification)');
            sendXray([FEATURE_NAME, 'not-supported', 'native']);
            return;
        }

        setupWebPushRuntime();
        return;
    }

    // fcm: firebase SDK грузится динамически внутри initFcmMessaging.
    log('Provider path: fcm (firebase)');
    try {
        const messaging = await initFcmMessaging();

        if (messaging) {
            setupWebPushRuntime(messaging);
        }
    } catch (error) {
        log(error);
    }
}
